import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Box, Grid, Chip, Divider, Container, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Page from '../../components/Page';
import BackButton from '../../components/BackButton';
import DocRequestForm from '../../sections/@dashboard/docRequest/DocRequestForm';
import tracker from '../../api/tracker';
import FreeSelect from '../../components/hook-form/FreeSelect';
import CommentReplyBox from '../../components/CommentReplyBox';
import useNotification from '../../hooks/useNotificaction';
import { fDateOnly } from '../../utils/formatTime';
import PrintButton from '../../components/print/PrintButton';
import DocRequestPrintTemplate from '../../sections/@dashboard/docRequest/DocRequestPrintTemplate';
import DocumentPaymentPanel from '../../sections/@dashboard/docRequest/DocumentPaymentPanel';

const override = { margin: '0 auto' };

const statusColors = {
  pending: { color: '#6d8ee9', bg: '#e0e9fa' },
  accepted: { color: '#6ca181', bg: '#dff8e7' },
  rejected: { color: '#FF003F', bg: '#F8E3E1' },
};

export default function DocRequestView() {
  const { hashid } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [docRequest, setDocRequest] = useState(null);
  const [sendNotification] = useNotification();

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const response = await tracker.get(`docRequests/${hashid}`);
    setDocRequest(response.data.data);
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [hashid]);

  const onStatusChange = async (status) => {
    await tracker.post(`docRequests/${hashid}/setStatus`, { status });
    sendNotification({ msg: t('status_updated'), variant: 'success' });
    fetchData();
  };

  const d = docRequest;

  return (
    <>
      <DocRequestPrintTemplate docRequest={d} />
      <Page title={t('doc_Requests')}>
      <Container maxWidth="xl">
        {loading || !d ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <FadeLoader css={override} color="#032C65" />
          </Box>
        ) : (
          <Card sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <BackButton onClick={() => navigate('/dashboard/docRequest')} />
              <Typography variant="h5" fontWeight={700}>
                {d.matrimony ? `${d.bride_name} & ${d.groom_name}` : d.recipient_name}
              </Typography>
              <Chip
                label={t(d.status)}
                size="small"
                sx={{
                  fontWeight: 600,
                  color: statusColors[d.status]?.color || '#374151',
                  backgroundColor: statusColors[d.status]?.bg || '#f3f4f6',
                }}
              />
              <Chip
                icon={d.donation_made ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                label={d.donation_made ? t('donation_paid') : t('donation_unpaid')}
                sx={{
                  fontWeight: 700,
                  color: d.donation_made ? '#166534' : '#92400e',
                  backgroundColor: d.donation_made ? '#dcfce7' : '#fef3c7',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                <PrintButton />
                <FreeSelect
                  initialValue={d.status}
                  onChange={(e) => onStatusChange(e.target.value)}
                  options={[
                    { value: 'accepted', label: t('accepted') },
                    { value: 'pending', label: t('pending') },
                    { value: 'rejected', label: t('rejected') },
                  ]}
                />
              </Box>
            </Box>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{
                    p: 3,
                    height: '100%',
                    border: '2px solid',
                    borderColor: d.donation_made ? '#86efac' : '#fbbf24',
                    bgcolor: d.donation_made ? '#f0fdf4' : '#fffbeb',
                  }}
                >
                  <DocumentPaymentPanel
                    hashid={d.hashid}
                    email={d.email}
                    name={d.requestor_name || d.recipient_name}
                    donationMade={d.donation_made}
                    receiptNumber={d.receipt_number}
                    paymentDate={d.payment_date}
                    onPaidChange={() => fetchData({ silent: true })}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <SectionHeader label={t('send_certificate')} />
                  <DocRequestForm docRequest={d} />
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <SectionHeader label={t('sacrament_info')} />
                  <InfoRow label={t('certificate')} value={t(d.type)} />
                  <InfoRow label={t('conferred_date')} value={fDateOnly(d.conferred_date, i18n.language)} />
                  {d.matrimony ? (
                    <>
                      <InfoRow label={t('bride_name')} value={d.bride_name} />
                      <InfoRow label={t('groom_name')} value={d.groom_name} />
                    </>
                  ) : (
                    <>
                      <InfoRow label={t('recipient_name')} value={d.recipient_name} />
                      <InfoRow label={t('recipient_birth')} value={fDateOnly(d.recipient_birth, i18n.language)} />
                    </>
                  )}
                </Card>
              </Grid>

              {!d.matrimony && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3, height: '100%' }}>
                    <SectionHeader label={t('family_info')} />
                    <InfoRow label={t('recipient_mother')} value={d.recipient_mother} />
                    <InfoRow label={t('recipient_father')} value={d.recipient_father} />
                  </Card>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <SectionHeader label={t('additional_info')} />
                  {d.confirmation ? (
                    <InfoRow label={t('sponsor_name')} value={d.sponsor_name} />
                  ) : (
                    <>
                      <InfoRow label={t('godfather_name')} value={d.godfather_name} />
                      <InfoRow label={d.matrimony ? t('godmother_name') : t('godmother_name')} value={d.godmother_name} />
                    </>
                  )}
                  {!d.matrimony && <InfoRow label={t('saint_name')} value={d.saint_name} />}
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3, height: '100%' }}>
                  <SectionHeader label={t('contact_info')} />
                  <InfoRow label={t('requestor_name')} value={d.requestor_name} />
                  <InfoRow label={t('recipient_relationship')} value={d.recipient_relationship} />
                  <InfoRow label={t('phone')} value={d.phone} />
                  <InfoRow label={t('email')} value={d.email} />
                  <InfoRow label={t('method')} value={d.method} />
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ p: 3 }}>
                  <SectionHeader label={t('address')} />
                  <InfoRow label={t('address_line_1')} value={d.address_line_1} />
                  <InfoRow label={t('address_line_2')} value={d.address_line_2} />
                  <InfoRow label={t('city')} value={d.city} />
                  <InfoRow label={t('state')} value={d.state} />
                  <InfoRow label={t('zip')} value={d.zip} />
                </Card>
              </Grid>

              {d.comments && (
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 3 }}>
                    <SectionHeader label={t('comments')} />
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>{d.comments}</Typography>
                    <CommentReplyBox
                      hashid={d.hashid}
                      endpoint="docRequests"
                      existingReply={d.comment_reply}
                      recipientEmail={d.email}
                    />
                  </Card>
                </Grid>
              )}
            </Grid>
          </Card>
        )}
      </Container>
      </Page>
    </>
  );
}

function SectionHeader({ label }) {
  return (
    <>
      <Typography
        variant="subtitle2"
        color="primary"
        fontWeight={700}
        sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}
      >
        {label}
      </Typography>
      <Divider sx={{ mb: 2 }} />
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}
